import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, FileText, Sparkles } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { asArray, http } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { cn } from "@/lib/utils";

export function SpotlightCard({ brand, index = 0, wide, preview = false }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  const Tag = preview ? "div" : Link;
  const tagProps = preview ? {} : { to: `/brands/${brand.slug}`, onClick: () => { http.post(`/brands/${brand.id}/featured-click`).catch(() => {}); } };
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }} className={cn("ph-featured-ring rounded-[2rem] p-[1.5px]", wide && "md:col-span-2")}>
      <Tag ref={ref} {...tagProps} onMouseMove={onMove} data-testid={`featured-brand-${brand.slug}`}
        className="ph-featured-card group relative flex h-full min-h-[280px] flex-col overflow-hidden rounded-[calc(2rem-1.5px)] bg-[#101625] p-7 text-white md:p-9">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#7388FF]/30 blur-[80px] transition-transform duration-700 group-hover:scale-125" />
        <div className="relative flex items-start justify-between gap-4">
          <BrandLogo brand={brand} className="h-16 w-16 rounded-2xl ring-1 ring-white/20" textClass="text-lg" />
          <span className="rounded-full border border-[#B6FF5C]/50 bg-[#B6FF5C]/15 px-3 py-1 font-mono-ph text-[10px] font-bold uppercase tracking-[0.18em] text-[#B6FF5C]" data-testid={`featured-sponsored-${brand.slug}`}>Sponsored</span>
        </div>
        <h3 className="relative mt-8 font-display text-4xl font-extrabold leading-none tracking-[-0.03em] md:text-5xl">{brand.name}</h3>
        <p className="relative mt-3 max-w-xl text-sm leading-relaxed text-slate-300 md:text-base">
          {brand.featured_tagline || brand.description || `Latest ${brand.name} price lists, all in one place.`}
        </p>
        <div className="relative mt-auto flex flex-wrap items-center justify-between gap-4 pt-8">
          <span className="flex items-center gap-2 font-mono-ph text-xs text-slate-300"><FileText className="h-4 w-4 text-[#7388FF]" />{brand.doc_count ?? 0} price lists · {(brand.categories ?? []).slice(0, 2).join(" · ")}</span>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#F6F8FC] px-5 py-2.5 text-sm font-semibold text-[#101625] transition-[background-color,transform] duration-300 group-hover:-translate-y-0.5 group-hover:bg-[#B6FF5C]">
            Explore {brand.name} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
          </span>
        </div>
      </Tag>
    </motion.div>
  );
}

export function FeaturedBrands() {
  const { data } = useFetch("/brands/featured", []);
  const brands = asArray(data);
  const ids = brands.map((b) => b.id).join(",");
  useEffect(() => {
    if (!ids || sessionStorage.getItem(`ph_fv_${ids}`)) return;
    sessionStorage.setItem(`ph_fv_${ids}`, "1");
    http.post("/brands/featured-views", { brand_ids: ids.split(",") }).catch(() => {});
  }, [ids]);
  if (!brands.length) return null;
  const odd = brands.length % 2 === 1;
  return (
    <section className="mx-auto max-w-7xl px-5 pt-24 md:px-8 md:pt-32" data-testid="featured-section">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.8 }}
        className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="mb-4 flex items-center gap-2 font-mono-ph text-xs uppercase tracking-[0.2em] text-ph-accent"><Sparkles className="h-4 w-4 text-ph-accent" /> Spotlight</p>
          <h2 className="font-display text-3xl font-extrabold leading-[1] tracking-[-0.03em] text-ph-ink sm:text-4xl lg:text-5xl">Sponsored brands</h2>
        </div>
        <p className="max-w-sm text-sm text-ph-muted">Sponsored placements from brands promoting with PRICEHUB. <Link to="/promote" className="font-semibold text-ph-accent hover:underline" data-testid="featured-promote-link">Promote your brand</Link></p>
      </motion.div>
      <div className="grid gap-5 md:grid-cols-2">
        {brands.map((b, i) => <SpotlightCard key={b.id} brand={b} index={i} wide={odd && i === 0} />)}
      </div>
    </section>
  );
}
